import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { LoadingButton } from "@mui/lab";
import bankInfoService from "../../../services/bankInfoService";
import uploadService from "../../../services/upload";
import {
    Card,
    CardContent,
    CardActions,
    Box,
    Breadcrumbs,
    TextField,
    Button,
    Typography,
    CardMedia,
    Container,
    Grid,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { instanceToken, BACKEND_URL } from "../../../utils/constant";

const imgFileTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];

export default function CreateBankInfo({ homeAlert }) {
    const navigate = useNavigate();
    const [imagePreview, setImagePreview] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [qrPreview, setQrPreview] = useState("");
    const [qrFile, setQrFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState({
        name: "",
        image_url: "",
        qr_image_url: "",
        entry_type: "bank",
        account_name: "",
        account_type: "",
        accounts: [{ account_number: "", type: "" }],
    });

    const isMmqr = formValues.entry_type === "mmqr";

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const handleAccountChange = (index, e) => {
        const { name, value } = e.target;
        const newAccounts = [...formValues.accounts];
        newAccounts[index][name] = value;
        setFormValues({ ...formValues, accounts: newAccounts });
    };

    const addAccount = () => {
        setFormValues({
            ...formValues,
            accounts: [...formValues.accounts, { account_number: "", type: "" }],
        });
    };

    const removeAccount = (index) => {
        if (formValues.accounts.length > 1) {
            const newAccounts = formValues.accounts.filter((_, i) => i !== index);
            setFormValues({ ...formValues, accounts: newAccounts });
        }
    };

    const changeFile = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const img = e.target.files[0];
            if (!imgFileTypes.includes(img.type)) {
                homeAlert("Invalid file type!", true);
                return;
            }
            if (img.size > 500000) {
                homeAlert("Image must be smaller than 500KB!", true);
                return;
            }
            setImageFile(img);
            setImagePreview(URL.createObjectURL(img));
        }
    };

    const changeQrFile = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const img = e.target.files[0];
            if (!imgFileTypes.includes(img.type)) {
                homeAlert("Invalid file type!", true);
                return;
            }
            if (img.size > 2000000) {
                homeAlert("QR image must be smaller than 2MB!", true);
                return;
            }
            setQrFile(img);
            setQrPreview(URL.createObjectURL(img));
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isMmqr && !qrFile && !formValues.qr_image_url) {
                homeAlert("QR image is required for MMQR entries!", true);
                setLoading(false);
                return;
            }

            let finalImageUrl = formValues.image_url;
            let finalQrUrl = formValues.qr_image_url;

            if (imageFile) {
                const uploadRes = await uploadService.uploadLocalImage(instanceToken.token, imageFile);
                finalImageUrl = `${BACKEND_URL}public/bank_infos/${uploadRes.data.imageName}`;
            }

            if (qrFile) {
                const uploadRes = await uploadService.uploadLocalImage(instanceToken.token, qrFile);
                finalQrUrl = `${BACKEND_URL}public/bank_infos/${uploadRes.data.imageName}`;
            }

            const accounts = (formValues.accounts || [])
                .filter((acc) => acc.account_number && String(acc.account_number).trim())
                .map((acc) => ({
                    account_number: acc.account_number,
                    type: acc.type || "",
                }));

            const payload = {
                name: formValues.name,
                image_url: finalImageUrl || null,
                qr_image_url: finalQrUrl || null,
                entry_type: formValues.entry_type,
                account_name: formValues.account_name || null,
                account_type: formValues.account_type || null,
                accounts,
            };

            await bankInfoService.postBankInfo(instanceToken.token, payload);
            homeAlert("Bank Info created successfully!", false);
            navigate("/bank-infos");
        } catch (error) {
            console.error(error);
            homeAlert(error?.response?.data?.error || "Error creating bank info!", true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container sx={{ py: 4 }}>
            <div role="presentation" style={{ marginBottom: "20px" }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link to="/bank-infos" style={{ textDecoration: "none", color: "inherit" }}>
                        Bank Information
                    </Link>
                    <Typography color="text.primary">Create Bank Info</Typography>
                </Breadcrumbs>
            </div>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Card sx={{ maxWidth: 800, width: "100%", borderRadius: 2 }}>
                    <form onSubmit={handleCreate}>
                        <CardContent>
                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                New Bank Entry
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 3 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="entry-type-label">Entry Type</InputLabel>
                                    <Select
                                        labelId="entry-type-label"
                                        name="entry_type"
                                        label="Entry Type"
                                        value={formValues.entry_type}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="bank">Bank</MenuItem>
                                        <MenuItem value="mmqr">MMQR</MenuItem>
                                    </Select>
                                </FormControl>

                                <Box sx={{ textAlign: "center" }}>
                                    {imagePreview && (
                                        <CardMedia
                                            component="img"
                                            image={imagePreview}
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                objectFit: "contain",
                                                margin: "0 auto",
                                                mb: 2,
                                                border: "1px solid #ddd",
                                                borderRadius: 2,
                                            }}
                                            crossOrigin="anonymous"
                                        />
                                    )}
                                    <Button variant="outlined" component="label" startIcon={<PhotoCamera />}>
                                        Upload Bank Logo
                                        <input hidden accept="image/*" type="file" onChange={changeFile} />
                                    </Button>
                                </Box>

                                <Box sx={{ textAlign: "center" }}>
                                    {qrPreview && (
                                        <CardMedia
                                            component="img"
                                            image={qrPreview}
                                            sx={{
                                                width: 160,
                                                height: 160,
                                                objectFit: "contain",
                                                margin: "0 auto",
                                                mb: 2,
                                                border: "1px solid #ddd",
                                                borderRadius: 2,
                                                bgcolor: "#fff",
                                            }}
                                            crossOrigin="anonymous"
                                        />
                                    )}
                                    <Button
                                        variant={isMmqr ? "contained" : "outlined"}
                                        component="label"
                                        startIcon={<QrCode2Icon />}
                                    >
                                        Upload QR Image {isMmqr ? "(Required)" : "(Optional)"}
                                        <input hidden accept="image/*" type="file" onChange={changeQrFile} />
                                    </Button>
                                </Box>

                                <TextField
                                    fullWidth
                                    label={isMmqr ? "MMQR Title" : "Bank Name"}
                                    name="name"
                                    required
                                    value={formValues.name}
                                    onChange={handleChange}
                                    placeholder={isMmqr ? "e.g. MMQR Payment" : "e.g. CB Bank"}
                                />

                                <TextField
                                    fullWidth
                                    label={isMmqr ? "Merchant Name (Optional)" : "Account Name (Shared)"}
                                    name="account_name"
                                    required={!isMmqr}
                                    value={formValues.account_name}
                                    onChange={handleChange}
                                    placeholder={isMmqr ? "e.g. CB Life (HO)" : "e.g. CB LIFE INSURANCE COMPANY LTD"}
                                />

                                {!isMmqr && (
                                    <TextField
                                        fullWidth
                                        label="Account Type (Shared)"
                                        name="account_type"
                                        value={formValues.account_type}
                                        onChange={handleChange}
                                        placeholder="e.g. Quick Pay Account"
                                    />
                                )}

                                <Divider>
                                    <Typography variant="overline" color="text.secondary">
                                        Accounts {isMmqr ? "(Optional)" : ""}
                                    </Typography>
                                </Divider>

                                {formValues.accounts.map((account, index) => (
                                    <Box key={index} sx={{ border: "1px solid #eee", p: 2.5, borderRadius: 2 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 2,
                                                bgcolor: "#F8F9FA",
                                                p: 1.5,
                                                borderRadius: 1,
                                                borderLeft: "4px solid #1976d2",
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "#1976d2",
                                                    fontWeight: 800,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 1,
                                                }}
                                            >
                                                Account {index + 1}
                                            </Typography>
                                            {formValues.accounts.length > 1 && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => removeAccount(index)}
                                                    sx={{
                                                        bgcolor: "white",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                                        "&:hover": { bgcolor: "#FFF5F5" },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Account Number"
                                                    name="account_number"
                                                    required={!isMmqr}
                                                    value={account.account_number}
                                                    onChange={(e) => handleAccountChange(index, e)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Type (e.g. Saving)"
                                                    name="type"
                                                    value={account.type}
                                                    onChange={(e) => handleAccountChange(index, e)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}

                                <Button
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    onClick={addAccount}
                                    sx={{ alignSelf: "flex-start" }}
                                >
                                    Add Another Account
                                </Button>
                            </Box>
                        </CardContent>
                        <CardActions sx={{ justifyContent: "flex-end", p: 3 }}>
                            <Button component={Link} to="/bank-infos">
                                Cancel
                            </Button>
                            <LoadingButton
                                type="submit"
                                variant="contained"
                                loading={loading}
                                color="primary"
                            >
                                Create Entry
                            </LoadingButton>
                        </CardActions>
                    </form>
                </Card>
            </Box>
        </Container>
    );
}
